import { AdTemplate, BRAND, createBackground, imageSlot, shape, text, unit } from './shared';

/** Pierwszy komplet szablonów: korzyść, wynik, funkcja, opinia, spotkanie. */
export const CORE_TEMPLATES: AdTemplate[] = [

  {
    id: 'value',
    label: 'Korzyść dla hotelu',
    description: 'Obietnica wartości i wezwanie do umówienia prezentacji',
    preview: { from: BRAND.deep, to: BRAND.teal, accent: BRAND.gold },
    build: (width, height) => {
      const u = unit(width, height);
      const margin = width * 0.08;
      const contentWidth = width - margin * 2;
      return {
        background: {
          ...createBackground(),
          kind: 'gradient',
          gradientFrom: BRAND.deep,
          gradientTo: BRAND.teal,
          gradientAngle: 135,
        },
        elements: [
          text('Marka', {
            text: 'Hotelspot',
            x: margin,
            y: height * 0.12,
            width: contentWidth,
            fontSize: u * 0.035,
            fontWeight: 700,
            uppercase: true,
            letterSpacing: u * 0.01,
            align: 'left',
            color: BRAND.gold,
          }),
          text('Nagłówek', {
            text: 'Gość zamawia z pokoju, recepcja ma spokój',
            x: margin,
            y: height * 0.24,
            width: contentWidth,
            fontSize: u * 0.085,
            fontWeight: 900,
            align: 'left',
            lineHeight: 1.08,
          }),
          text('Opis', {
            text: 'Cyfrowy hub gościa: room service, spa, informacje o obiekcie i zamówienia bez telefonu do recepcji.',
            x: margin,
            y: height * 0.58,
            width: contentWidth,
            fontSize: u * 0.03,
            fontWeight: 400,
            align: 'left',
            lineHeight: 1.45,
            color: '#dbeae6',
          }),
          text('Przycisk', {
            text: 'Umów prezentację',
            x: margin,
            y: height * 0.79,
            width: width * 0.5,
            fontSize: u * 0.032,
            fontWeight: 800,
            uppercase: true,
            color: BRAND.deep,
            background: BRAND.gold,
            backgroundRadius: 999,
            padding: u * 0.026,
          }),
        ],
      };
    },
  },

  {
    id: 'metric',
    label: 'Wynik liczbowy',
    description: 'Jedna mocna liczba jako dowód skuteczności wdrożenia',
    preview: { from: BRAND.teal, to: BRAND.mint, accent: '#ffffff' },
    build: (width, height) => {
      const u = unit(width, height);
      const margin = width * 0.08;
      const contentWidth = width - margin * 2;
      return {
        background: {
          ...createBackground(),
          kind: 'gradient',
          gradientFrom: BRAND.deep,
          gradientTo: BRAND.mint,
          gradientAngle: 150,
        },
        elements: [
          text('Liczba', {
            text: '+31%',
            x: margin,
            y: height * 0.2,
            width: contentWidth,
            fontSize: u * 0.22,
            fontFamily: 'Anton',
            fontWeight: 400,
            lineHeight: 1,
            color: '#ffffff',
          }),
          text('Podpis liczby', {
            text: 'więcej zamówień room service w pierwszym kwartale po wdrożeniu',
            x: width * 0.15,
            y: height * 0.48,
            width: width * 0.7,
            fontSize: u * 0.033,
            fontWeight: 600,
            lineHeight: 1.4,
            color: '#d7f0e7',
          }),
          shape('Linia', {
            x: width * 0.42,
            y: height * 0.68,
            width: width * 0.16,
            height: Math.max(3, u * 0.008),
            fill: BRAND.gold,
          }),
          text('Marka', {
            text: 'Hotelspot dla hoteli',
            x: margin,
            y: height * 0.76,
            width: contentWidth,
            fontSize: u * 0.03,
            fontWeight: 700,
            uppercase: true,
            letterSpacing: u * 0.008,
            color: BRAND.gold,
          }),
        ],
      };
    },
  },

  {
    id: 'feature',
    label: 'Funkcja produktu',
    description: 'Pas marki z boku i miejsce na zrzut ekranu aplikacji',
    preview: { from: '#ffffff', to: BRAND.light, accent: BRAND.teal },
    build: (width, height) => {
      const u = unit(width, height);
      const bandWidth = width * 0.42;
      const inset = width * 0.06;
      const contentWidth = bandWidth - inset * 2;
      return {
        background: { ...createBackground(), kind: 'solid', color: BRAND.light },
        elements: [
          shape('Pas marki', { x: 0, y: 0, width: bandWidth, height, fill: BRAND.deep }),
          text('Nadtytuł', {
            text: 'Nowość w panelu',
            x: inset,
            y: height * 0.16,
            width: contentWidth,
            fontSize: u * 0.028,
            fontWeight: 700,
            uppercase: true,
            letterSpacing: u * 0.008,
            align: 'left',
            color: BRAND.gold,
          }),
          text('Nazwa funkcji', {
            text: 'Płatności online w zamówieniach',
            x: inset,
            y: height * 0.28,
            width: contentWidth,
            fontSize: u * 0.06,
            fontWeight: 900,
            align: 'left',
            lineHeight: 1.12,
          }),
          text('Opis funkcji', {
            text: 'Gość płaci kartą przy zamówieniu, a rozliczenie trafia prosto na konto obiektu.',
            x: inset,
            y: height * 0.62,
            width: contentWidth,
            fontSize: u * 0.026,
            fontWeight: 400,
            align: 'left',
            lineHeight: 1.5,
            color: '#cfdedb',
          }),
          imageSlot('Miejsce na zrzut ekranu', {
            x: bandWidth + inset,
            y: height * 0.18,
            width: width - bandWidth - inset * 2,
            height: height * 0.64,
            radius: u * 0.03,
          }),
        ],
      };
    },
  },

  {
    id: 'testimonial',
    label: 'Opinia hotelarza',
    description: 'Cytat od klienta, czyli dowód od osoby z branży',
    preview: { from: BRAND.deep, to: '#123f3f', accent: BRAND.gold },
    build: (width, height) => {
      const u = unit(width, height);
      const margin = width * 0.1;
      const contentWidth = width - margin * 2;
      return {
        background: { ...createBackground(), kind: 'solid', color: BRAND.deep },
        elements: [
          text('Cudzysłów', {
            text: '"',
            x: margin,
            y: height * 0.1,
            width: contentWidth,
            fontSize: u * 0.18,
            fontFamily: 'Playfair Display',
            fontWeight: 700,
            align: 'left',
            color: BRAND.gold,
            opacity: 0.7,
          }),
          text('Cytat', {
            text: 'Recepcja odbiera o połowę mniej telefonów, a zamówienia z pokoi wreszcie się nie gubią.',
            x: margin,
            y: height * 0.3,
            width: contentWidth,
            fontSize: u * 0.05,
            fontFamily: 'Playfair Display',
            fontWeight: 400,
            fontStyle: 'italic',
            align: 'left',
            lineHeight: 1.35,
          }),
          shape('Kreska', {
            x: margin,
            y: height * 0.7,
            width: width * 0.1,
            height: Math.max(3, u * 0.007),
            fill: BRAND.gold,
          }),
          text('Autor', {
            text: 'Dyrektor obiektu, hotel 4 gwiazdki',
            x: margin,
            y: height * 0.76,
            width: contentWidth,
            fontSize: u * 0.026,
            fontWeight: 600,
            uppercase: true,
            letterSpacing: u * 0.005,
            align: 'left',
            color: '#cfdedb',
          }),
        ],
      };
    },
  },

  {
    id: 'meeting',
    label: 'Spotkanie branżowe',
    description: 'Zaproszenie na targi, webinar albo demo dla hotelarzy',
    preview: { from: '#111111', to: BRAND.teal, accent: BRAND.gold },
    build: (width, height) => {
      const u = unit(width, height);
      const margin = width * 0.09;
      const frameInset = width * 0.05;
      const contentWidth = width - margin * 2;
      return {
        background: { ...createBackground(), kind: 'solid', color: BRAND.deep },
        elements: [
          shape('Ramka', {
            x: frameInset,
            y: frameInset,
            width: width - frameInset * 2,
            height: height - frameInset * 2,
            fill: '',
            stroke: BRAND.gold,
            strokeWidth: Math.max(2, u * 0.005),
          }),
          text('Termin', {
            text: 'Webinar, czwartek 18:00',
            x: margin,
            y: height * 0.19,
            width: contentWidth,
            fontSize: u * 0.03,
            fontWeight: 600,
            uppercase: true,
            letterSpacing: u * 0.008,
            color: BRAND.gold,
          }),
          text('Temat', {
            text: 'Jak zwiększyć przychód z pokoju bez dodatkowego etatu',
            x: margin,
            y: height * 0.33,
            width: contentWidth,
            fontSize: u * 0.058,
            fontFamily: 'Oswald',
            fontWeight: 700,
            lineHeight: 1.12,
          }),
          text('Szczegóły', {
            text: 'Case study wdrożeń w obiektach miejskich i resortach. Udział bezpłatny, liczba miejsc ograniczona.',
            x: margin,
            y: height * 0.66,
            width: contentWidth,
            fontSize: u * 0.026,
            fontWeight: 400,
            lineHeight: 1.45,
            color: '#cfdedb',
          }),
          text('Marka', {
            text: 'hotelspot.pl',
            x: margin,
            y: height * 0.85,
            width: contentWidth,
            fontSize: u * 0.028,
            fontWeight: 700,
            letterSpacing: u * 0.004,
            color: BRAND.gold,
          }),
        ],
      };
    },
  },
];
